import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type { ValidateFilterValuesResponseDto } from '@cdw-ae/common-data';
import {
  AueTimeFrame,
  BatteryHealth,
  type CdwGridFilter,
  DeviceAsset,
  DeviceDiskUsed,
  DeviceLastActiveTimeframe,
  DeviceStateAnalysisAsset,
  type EligibilityResultDto,
  GoogleDeviceData,
  parseDate,
  type SelectionMutationDto,
  SqlResponse,
  Vendor,
  Vendors,
} from '@cdw-ae/common-data';
import { ICdwSearchGridDataService } from '@cdw-ae/common-ui-components-search-grid';
import { selectCustomerSettings } from '@cdw-ae/customer-lookup';
import {
  IGetSqlDataParams,
  SelectedFilterField,
} from '@cdw-ae/filter-bar-interfaces';
import { type JobType } from '@cdw-ae/jobs-status-state';
import { getQueryStringFromParams } from '@cdw-ae/routes-shared-api';
import { Store } from '@ngrx/store';
import { catchError, map, Observable, of, switchMap, take } from 'rxjs';
import { setFilterSnapshotRef } from '../../../+state/filter/filter.actions';
import { selectFilterSnapshotRef } from '../../../+state/filter/filter.selectors';
import { VersionInfo } from '../../../components/charts/models/chart.model';
import {
  DevicesAutoUpdateExpirySummary,
  DevicesBatteryHealthSummary,
  DevicesLastActiveSummary,
  DevicesVersionSummary,
} from '../../../layouts/internal/components/dashboard/dashboard.model';
import { RootState } from '../../interfaces/rootState.interface';
import { ApiUrlBuilderService } from '../api-url-builder.service';
import { FilterBarId } from '../route-config/models/route-config.model';

export const httpOptions: { headers: HttpHeaders } = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'x-cdw-app-name': 'Gopher for Chrome WebApp',
  }),
};

/**
 * Build httpOptions optionally including the `x-cdw-grid-session` header.
 * Used by selection-aware calls (page-fetch enrichment + the 6 selection
 * endpoints) so the spog-backend → asset-service-layer → saas-device-api
 * chain can construct the per-tab Redis selection key. When `gridSessionId`
 * is undefined or empty, returns the standard httpOptions and the chain
 * falls back to its non-selection-aware code path (full backwards compat).
 */
function httpOptionsWithSession(gridSessionId?: string): {
  headers: HttpHeaders;
} {
  if (!gridSessionId) return httpOptions;
  return {
    headers: httpOptions.headers.set('x-cdw-grid-session', gridSessionId),
  };
}

/**
 * View-key constant for the device-search grid. Used as the
 * `?viewKey=...` query param on every selection endpoint call so the
 * backend Redis key stays scoped to this grid (other grids on the same
 * page in the same tab would use distinct viewKeys).
 */
const DEVICE_SEARCH_VIEW_KEY = 'device-search';

/**
 * NgRx filter-bar id for Device Search (route-config key) — distinct from the
 * selection `viewKey` above. Used to read/write the `filterSnapshotRef` state
 * when a large filter set is externalized (EDUS-2575).
 */
const DEVICE_SEARCH_FILTER_BAR_ID = FilterBarId.DeviceSearch;

/**
 * Inline-URL budget (chars). When the candidate request URL would exceed this,
 * the filter set is externalized to a content-addressed snapshot referenced by
 * `?filterSnapshotRef=` instead of inlined (EDUS-2575 §3). Sits well under the
 * ~2,083 cross-browser URL-portability limit and far under the ~16 KB server
 * header limit, so kept-inline links stay shareable and never 414.
 */
const INLINE_URL_BUDGET = 2000;

interface ApiResponse<T extends { id: string | number }> {
  // COMMON-910 the current backend returns devices: T[] and not data: T[], so this will need to be addressed on the back-end
  // to work with device search
  data: T[];
  success: boolean;
  nextPageToken?: string;
  error?: string;
}

@Injectable({
  providedIn: 'root',
})
export class DevicesDataService
  implements ICdwSearchGridDataService<DeviceAsset>
{
  private store: Store<RootState> = inject(Store);

  /**
   * Generation token guarding the create-on-overflow snapshot dispatch
   * (EDUS-2575, review sub-issue 2b). Bumped whenever the snapshot-eligible
   * filter set changes by content (see {@link lastSnapshotFiltersKey}). A
   * `createFilterSnapshot` POST captures the generation at kickoff; if filters
   * change before it resolves, the captured generation is stale and we skip
   * dispatching its now-superseded ref. Necessary because the SSRM datasource
   * never cancels an in-flight `getDevices` (it pipes only
   * `takeUntil(destroy$)`), so without this a late POST would re-populate the
   * store with a ref for filters the user already moved away from.
   */
  private snapshotGeneration = 0;

  /**
   * Content key of the last query's snapshot-eligible filter set — the lean
   * normalized payload, or `null` when the last query had no eligible filters
   * (cleared, or an id-array / selection query). Used to distinguish a real
   * filter-set change (bump the generation) from pagination/sort (same filters
   * → same key → no bump, so scrolling never invalidates an in-flight snapshot
   * POST). The `null` sentinel makes *leaving* eligibility a detectable change
   * too, not just switching between two eligible sets (sub-issue 2b).
   */
  private lastSnapshotFiltersKey: string | null = null;

  constructor(
    private httpClient: HttpClient,
    private readonly urlBuilder: ApiUrlBuilderService
  ) {}

  public fetchSqlData(
    params: IGetSqlDataParams
  ): Observable<SqlResponse<DeviceAsset>> {
    const {
      activeFilters,
      selectFields,
      startRow,
      endRow,
      sortOrder,
      gridSessionId,
      useSelectionAsFilter,
      idAllowList,
    } = params;

    // Phase 7 of EDUS-2485 deleted the legacy idAllowList short-circuit —
    // "Show Selected Only" now flows server-side via useSelectionAsFilter +
    // the gridSessionId header (the backend reads the user's Redis selection
    // set instead of the client serializing 300k IDs into a URL).
    //
    // EDUS-2509 reintroduced the idAllowList path for "Show changes only"
    // (cellChanges live purely client-side; there's no server state to
    // look up). Forwarded to getDevices below, which translates it into
    // the backend's existing wire format.
    return this.getDevices({
      activeFilters,
      selectFields,
      startRow,
      endRow,
      sortOrder,
      gridSessionId,
      useSelectionAsFilter,
      idAllowList,
    });
  }

  public getDevices(args: {
    activeFilters?: SelectedFilterField[];
    selectFields?: string[];
    startRow?: number;
    endRow?: number;
    sortOrder?: { name: string; direction?: 'ASC' | 'DESC' }[];
    /**
     * Per-grid-instance session UUID (Phase 6+ of EDUS-2484). When provided,
     * sent as the `x-cdw-grid-session` HTTP header so the backend chain
     * constructs the user's Redis selection key. Combined with the
     * `viewKey=device-search` query param (also sent below when present)
     * and the firebaseUserId from the spog-backend auth claims, the
     * 4-tuple (customerId, userId, viewKey, sessionUuid) keys the
     * server-side selection set.
     */
    gridSessionId?: string;
    /**
     * "Show selected only" toggle (Phase 7+ of EDUS-2485). When true, sent
     * as `useSelectionAsFilter=true` query param + `viewKey=device-search`.
     * The backend short-circuits to filtering by Redis selection-set
     * membership instead of the activeFilters chain.
     */
    useSelectionAsFilter?: boolean;
    /**
     * EDUS-2509: client-supplied row IDs to restrict the result set to.
     * When non-empty, overrides `activeFilters` with a JSON-stringified
     * array of IDs — the backend's `normalizeInputToQueryDto()` routes
     * that shape through `mapAssetIdsToQueryDto`. Used by the SSRM grid's
     * "Show changes only" UX (the IDs live in the consumer's cellChanges
     * Map and have no server-side representation, so we can't use the
     * useSelectionAsFilter path).
     */
    idAllowList?: string[];
  }): Observable<SqlResponse<DeviceAsset>> {
    const { gridSessionId, useSelectionAsFilter, idAllowList, ...queryArgs } =
      args;
    // EDUS-2509: when an idAllowList is supplied, override activeFilters
    // with the JSON-stringified id array. Same wire format as
    // `getFullDevicesByIds` (line ~319). The backend's
    // `normalizeInputToQueryDto()` differentiates by content shape —
    // array-of-strings routes through `mapAssetIdsToQueryDto` for an
    // id-only WHERE clause.
    const queryArgsWithIds =
      idAllowList && idAllowList.length > 0
        ? { ...queryArgs, activeFilters: JSON.stringify(idAllowList) }
        : queryArgs;
    // viewKey rides on the query string when selection signals are in play.
    // The backend requires both the header and viewKey to construct the
    // Redis key; sending one without the other falls back to the
    // non-selection-aware code path.
    const queryWithSelection = gridSessionId
      ? {
          ...queryArgsWithIds,
          viewKey: DEVICE_SEARCH_VIEW_KEY,
          ...(useSelectionAsFilter ? { useSelectionAsFilter: true } : {}),
        }
      : queryArgsWithIds;
    // EDUS-2575: snapshot path applies only to a real multi-value-capable
    // filter set — never the id-array ("show changes only") or selection
    // ("show selected only") paths, which carry no inlined filter blob.
    const isPlainFilterQuery = !(idAllowList?.length || useSelectionAsFilter);
    const snapshotEligibleFilters =
      isPlainFilterQuery &&
      Array.isArray(queryArgs.activeFilters) &&
      queryArgs.activeFilters.length > 0
        ? (queryArgs.activeFilters as SelectedFilterField[])
        : null;

    // Generation tracking spans ALL queries, not just snapshot-eligible ones.
    // Leaving eligibility — filters cleared, or a switch to the id-array
    // ("show changes only") / selection ("show selected only") paths — is itself
    // a filter-set change that must supersede any in-flight create-on-overflow
    // POST (EDUS-2575 sub-issue 2b). A non-eligible query carries the sentinel
    // key `null`; the null↔non-null transition bumps the generation, so a late
    // POST for the prior eligible set sees a stale generation and skips its
    // dispatch (otherwise it would re-write a ref the user has moved away from,
    // resurrecting the old filters on the next refresh/share). For an eligible
    // set the key is the LEAN normalized payload (what's actually shareable /
    // what a snapshot stores), not the full SelectedFilterField objects — those
    // carry field/matcher config that would overflow the budget for even 1-2
    // small filters. Pagination/sort reuse the same filters → same key → no
    // bump, so scrolling never invalidates an in-flight POST.
    const snapshotFiltersKey = snapshotEligibleFilters
      ? encodeURIComponent(
          JSON.stringify(this.toSnapshotPayload(snapshotEligibleFilters))
        )
      : null;
    if (snapshotFiltersKey !== this.lastSnapshotFiltersKey) {
      this.lastSnapshotFiltersKey = snapshotFiltersKey;
      this.snapshotGeneration++;
    }

    if (snapshotEligibleFilters) {
      // Derived state: present iff the current filters already overflowed (this
      // block or a prior one created the snapshot, or it was restored from URL).
      const currentRef = this.readSnapshotRef();
      if (currentRef) {
        return this.getDevicesBySnapshotRef(currentRef, args);
      }

      // snapshotFiltersKey is the eligible payload on this path; the truthiness
      // guard also narrows it to non-null for the length check.
      if (snapshotFiltersKey && snapshotFiltersKey.length > INLINE_URL_BUDGET) {
        // Create-on-overflow INSIDE the awaited fetch (plan §5): the grid simply
        // waits for this; it can never fire a doomed inline GET. Propagate the
        // ref to state so the URL-sync effect drops the inline param.
        const generation = this.snapshotGeneration;
        return this.createFilterSnapshot(snapshotEligibleFilters).pipe(
          switchMap(({ id }) => {
            // Only adopt the ref if filters haven't changed since this POST
            // started. The SSRM datasource doesn't cancel the in-flight
            // observable on filter change (only takeUntil(destroy$)), so a
            // late-resolving POST for a superseded filter set must NOT write
            // its ref back into the store (sub-issue 2b).
            if (this.snapshotGeneration === generation) {
              this.store.dispatch(
                setFilterSnapshotRef({
                  filterBarId: DEVICE_SEARCH_FILTER_BAR_ID,
                  snapshotRef: id,
                })
              );
            }
            return this.getDevicesBySnapshotRef(id, args);
          })
        );
      }
    }

    // EDUS-2575 (review item 4): send the LEAN payload inline — the same shape
    // we measure against the budget above and that a snapshot stores. The full
    // SelectedFilterField[] carries displayName/fieldMatchers/options/uid the
    // backend never reads (it needs only field.name, selectedMatcher.name,
    // matchValue — proven by the snapshot path, which expands to exactly this
    // shape and queries successfully). Serializing the full objects bloated the
    // inline URL to 10-20KB for multi-chip sets → server 414, AND made the
    // budget check measure a smaller payload than what actually went on the
    // wire. Mapping to the lean form here keeps measurement == wire.
    const inlineQuery = snapshotEligibleFilters
      ? {
          ...queryWithSelection,
          activeFilters: this.toSnapshotPayload(snapshotEligibleFilters),
        }
      : queryWithSelection;
    const paramsQueryString = getQueryStringFromParams(inlineQuery);

    return this.httpClient
      .get<SqlResponse<DeviceAsset>>(
        `${this.urlBuilder.backendApi('device-assets', '')}?${paramsQueryString}`,
        httpOptionsWithSession(gridSessionId)
      )
      .pipe(map(this.mapSqlResponse));
  }

  /**
   * Issue the device query carrying only a `filterSnapshotRef` (not the inlined
   * `activeFilters`). Used once a large filter set has been externalized — the
   * backend expands the snapshot before querying. Pagination/sort/selectFields
   * and the selection `viewKey` still ride inline (all small).
   */
  private getDevicesBySnapshotRef(
    filterSnapshotRef: string,
    args: {
      selectFields?: string[];
      startRow?: number;
      endRow?: number;
      sortOrder?: { name: string; direction?: 'ASC' | 'DESC' }[];
      gridSessionId?: string;
      useSelectionAsFilter?: boolean;
    }
  ): Observable<SqlResponse<DeviceAsset>> {
    const { gridSessionId, selectFields, startRow, endRow, sortOrder } = args;
    const query = {
      ...(selectFields ? { selectFields } : {}),
      ...(startRow !== undefined ? { startRow } : {}),
      ...(endRow !== undefined ? { endRow } : {}),
      ...(sortOrder ? { sortOrder } : {}),
      filterSnapshotRef,
      ...(gridSessionId ? { viewKey: DEVICE_SEARCH_VIEW_KEY } : {}),
    };
    const paramsQueryString = getQueryStringFromParams(query);
    return this.httpClient
      .get<SqlResponse<DeviceAsset>>(
        `${this.urlBuilder.backendApi('device-assets', '')}?${paramsQueryString}`,
        httpOptionsWithSession(gridSessionId)
      )
      .pipe(map(this.mapSqlResponse));
  }

  /** Shared SqlResponse mapper for both the inline and snapshot-backed paths. */
  private mapSqlResponse = (
    res: SqlResponse<DeviceAsset>
  ): SqlResponse<DeviceAsset> => ({
    itemTotal: res.itemTotal,
    data: res.data.map(this.mapDeviceAsset),
    meta: res.meta,
    ...((res as { selectionCount?: number }).selectionCount !== undefined
      ? { selectionCount: (res as { selectionCount?: number }).selectionCount }
      : {}),
  });

  /** Synchronously read the current device-search snapshot ref from the store. */
  private readSnapshotRef(): string | null {
    let ref: string | null = null;
    this.store
      .select(selectFilterSnapshotRef(DEVICE_SEARCH_FILTER_BAR_ID))
      .pipe(take(1))
      .subscribe((value) => {
        ref = value;
      });
    return ref;
  }

  /**
   * Create (or dedup to an existing) content-addressed filter snapshot from the
   * current filter set, returning its id. Sends the normalized payload (uid +
   * UI-only config stripped) — what the backend hashes/stores (EDUS-2575).
   */
  public createFilterSnapshot(
    filters: SelectedFilterField[]
  ): Observable<{ id: string }> {
    return this.httpClient.post<{ id: string }>(
      `${this.urlBuilder.backendApi('device-assets', 'filter-snapshots')}`,
      this.toSnapshotPayload(filters),
      httpOptions
    );
  }

  /**
   * The normalized, snapshot-storable form of a filter set: only
   * `field.name`, `selectedMatcher.name`, and `matchValue` (uid + UI-only field
   * config stripped). Used both to POST a snapshot and to size the overflow
   * check against what's actually shareable.
   */
  private toSnapshotPayload(filters: SelectedFilterField[]): {
    field: { name: string };
    selectedMatcher: { name: string };
    matchValue: SelectedFilterField['matchValue'];
  }[] {
    return filters.map((filter) => ({
      field: { name: filter.field.name },
      selectedMatcher: { name: filter.selectedMatcher.name },
      matchValue: filter.matchValue,
    }));
  }

  /** Resolve a filter snapshot id to its stored payload (for chip hydration). */
  public getFilterSnapshot(
    id: string
  ): Observable<{ payload: CdwGridFilter[] }> {
    return this.httpClient.get<{ payload: CdwGridFilter[] }>(
      `${this.urlBuilder.backendApi('device-assets', `filter-snapshots/${id}`)}`,
      httpOptions
    );
  }

  // Phase 9 of EDUS-2485 deleted the deprecated getAllDeviceIds stub
  // (formerly the materialize-IDs path that triggered PR #885's 500-cap
  // bug); the consumer now uses the selectByFilter callback below
  // + the server-side selection store.

  // === Server-side selection store API (EDUS-2485 Phase 8) ===
  // These 6 methods replace the legacy getAllDeviceIds (deleted) and
  // implement the consumer-side surface for the Redis-backed selection
  // store. The grid component (Phase 7) invokes them via its 5 callback
  // @Inputs; the consumer (Phase 9) wires those callbacks to these methods.
  // viewKey is hardcoded to 'device-search' since this service is the
  // device-search-specific data adapter.

  /**
   * Add all rows matching `filter` to the user's selection set. Backend
   * resolves the filter to ids server-side — no id array crosses the wire
   * (kills L1 from PR #885 by construction). Returns the new total count.
   * HTTP 400 from element-cap rejection (>500k) bubbles as the typical
   * Angular HttpErrorResponse — consumer maps to a snackbar.
   */
  public selectByFilter(
    filter: CdwGridFilter[],
    gridSessionId: string
  ): Observable<{ count: number }> {
    const body: SelectionMutationDto = { op: 'select-by-filter', filter };
    return this.postSelectionMutation(body, gridSessionId);
  }

  /**
   * Remove all rows matching `filter` from the user's selection set. Same
   * cap rule applies (rejection bubbles).
   */
  public deselectByFilter(
    filter: CdwGridFilter[],
    gridSessionId: string
  ): Observable<{ count: number }> {
    const body: SelectionMutationDto = { op: 'deselect-by-filter', filter };
    return this.postSelectionMutation(body, gridSessionId);
  }

  /**
   * Add a batch of ids to the user's selection set. Used for the consumer's
   * 250 ms-debounced batch sync of individual checkbox clicks; the grid
   * component fires `(selectionToggled)` per click and the consumer
   * groups them by op + flushes once per debounce window.
   */
  public selectByIds(
    ids: string[],
    gridSessionId: string
  ): Observable<{ count: number }> {
    const body: SelectionMutationDto = { op: 'select-many', assetIds: ids };
    return this.postSelectionMutation(body, gridSessionId);
  }

  /**
   * Remove a batch of ids from the user's selection set. Mirror of
   * `selectByIds`.
   */
  public deselectByIds(
    ids: string[],
    gridSessionId: string
  ): Observable<{ count: number }> {
    const body: SelectionMutationDto = { op: 'deselect-many', assetIds: ids };
    return this.postSelectionMutation(body, gridSessionId);
  }

  /**
   * Drop the entire user selection set on the server. Used by the grid's
   * `clearSelection()` (banner "Clear selection" link, etc.) and on filter
   * changes (filter-bar emit invalidates cross-page selection).
   */
  public clearSelection(gridSessionId: string): Observable<{ count: number }> {
    // Angular's HttpClient.delete carries the shared httpOptions which has
    // Content-Type: application/json. spog-backend's Fastify content-type
    // parser rejects DELETE with that header but no body
    // ("Body cannot be empty when content-type is set to 'application/json'"),
    // so we send an empty {} to satisfy the parser. Endpoint ignores the body.
    return this.httpClient.delete<{ count: number }>(
      `${this.urlBuilder.backendApi('device-assets', 'selection')}?viewKey=${encodeURIComponent(DEVICE_SEARCH_VIEW_KEY)}`,
      { ...httpOptionsWithSession(gridSessionId), body: {} }
    );
  }

  /**
   * Read the current selection count without mutating. Used for footer
   * reconciliation after page reload or after a selection-write failure +
   * snackbar retry. Read failures degrade gracefully on the backend
   * (returns 0); transport failures bubble through Angular's HttpClient.
   */
  public getSelectionCount(
    gridSessionId: string
  ): Observable<{ count: number }> {
    return this.httpClient.get<{ count: number }>(
      `${this.urlBuilder.backendApi('device-assets', 'selection/count')}?viewKey=${encodeURIComponent(DEVICE_SEARCH_VIEW_KEY)}`,
      httpOptionsWithSession(gridSessionId)
    );
  }

  public evaluateSelectionEligibility(
    actionType: string,
    gridSessionId: string,
    opts?: Record<string, unknown>
  ): Observable<EligibilityResultDto> {
    return this.httpClient.post<EligibilityResultDto>(
      `${this.urlBuilder.backendApi('device-assets', 'selection/eligibility')}?viewKey=${encodeURIComponent(DEVICE_SEARCH_VIEW_KEY)}`,
      { actionType, opts },
      httpOptionsWithSession(gridSessionId)
    );
  }

  /**
   * Common POST shape for the 4 selection mutations that go through
   * POST /api/device-assets/selection. Body shape comes from the
   * discriminated SelectionMutationDto (Phase 0); transport pattern is
   * identical regardless of which `op` variant.
   */
  private postSelectionMutation(
    body: SelectionMutationDto,
    gridSessionId: string
  ): Observable<{ count: number }> {
    return this.httpClient.post<{ count: number }>(
      `${this.urlBuilder.backendApi('device-assets', 'selection')}?viewKey=${encodeURIComponent(DEVICE_SEARCH_VIEW_KEY)}`,
      body,
      httpOptionsWithSession(gridSessionId)
    );
  }

  /**
   * Fetch full device records by IDs in a single request.
   * Uses the activeFilters query param with a string array of IDs,
   * which the backend's normalizeInputToQueryDto() handles via mapAssetIdsToQueryDto().
   */
  public getFullDevicesByIds(deviceIds: string[]): Observable<DeviceAsset[]> {
    if (!deviceIds?.length) {
      return of([]);
    }

    const params = {
      activeFilters: JSON.stringify(deviceIds),
      maxResults: deviceIds.length,
      startRow: 0,
      endRow: deviceIds.length,
    };

    const paramsQueryString = getQueryStringFromParams(params);

    return this.httpClient
      .get<SqlResponse<DeviceAsset>>(
        `${this.urlBuilder.backendApi('device-assets', '')}?${paramsQueryString}`,
        { ...httpOptions }
      )
      .pipe(
        map((res: SqlResponse<DeviceAsset>) =>
          res.data.map(this.mapDeviceAsset)
        )
      );
  }

  /**
   * Fetch devices by their vendor (Google Directory) IDs in a single request
   *
   * Gopher Buddy sessions carry Google's device ID, which SPOG stores as
   * `vendorId` — not the asset `id` that `getDeviceById` expects. Both are
   * UUID-shaped, so looking one up by the other silently returns nothing.
   *
   * @param vendorIds Array of Google device IDs to fetch
   * @returns Observable of DeviceAsset array; empty on error or no input
   */
  public getDevicesByVendorIds(vendorIds: string[]): Observable<DeviceAsset[]> {
    if (!vendorIds || vendorIds.length === 0) {
      return of([]);
    }

    const params = {
      activeFilters: JSON.stringify([
        {
          field: { displayName: 'Vendor ID', name: 'vendorId' },
          selectedMatcher: { name: 'oneOf' },
          matchValue: vendorIds,
        },
      ]),
      selectFields: JSON.stringify([
        'id',
        'vendorId',
        'serialNumber',
        'model',
        'vendorProperties.google.status',
        'vendorProperties.google.lastSync',
        'vendorProperties.google.osVersion',
        'vendorProperties.google.orgUnitPath',
        'vendorProperties.google.annotatedUser',
        'vendorProperties.google.annotatedLocation',
      ]),
      startRow: 0,
      endRow: vendorIds.length,
    };

    return this.httpClient
      .get<SqlResponse<DeviceAsset>>(
        `${this.urlBuilder.backendApi('device-assets', '')}?${getQueryStringFromParams(params)}`,
        { ...httpOptions }
      )
      .pipe(
        map((res: SqlResponse<DeviceAsset>) =>
          res.data.map(this.mapDeviceAsset)
        ),
        catchError(() => of([]))
      );
  }

  /**
   * Fetch devices by their IDs
   * Used for bulk edit validation to get current field values
   * @param deviceIds Array of device IDs to fetch
   * @returns Observable of DeviceAsset array
   */
  public getDevicesByIds(deviceIds: string[]): Observable<DeviceAsset[]> {
    if (!deviceIds || deviceIds.length === 0) {
      return of([]);
    }

    // Build query params directly for the device IDs filter
    const params = {
      activeFilters: JSON.stringify(deviceIds),
      selectFields: JSON.stringify([
        'id',
        'vendorProperties.google.notes',
        'vendorProperties.google.annotatedAssetId',
        'vendorProperties.google.annotatedUser',
        'vendorProperties.google.annotatedLocation',
        'vendorProperties.google.status',
        'vendorProperties.google.osVersion',
        'vendorProperties.google.autoUpdateExpiration',
        'vendorProperties.google.orgUnitPath',
      ]),
    };

    const paramsQueryString = getQueryStringFromParams(params);

    return this.httpClient
      .get<SqlResponse<DeviceAsset>>(
        `${this.urlBuilder.backendApi('device-assets', '')}?${paramsQueryString}`,
        { ...httpOptions }
      )
      .pipe(
        map((res: SqlResponse<DeviceAsset>) =>
          res.data.map(this.mapDeviceAsset)
        ),
        catchError(() => of([]))
      );
  }

  private mapDeviceAsset(device: DeviceAsset): DeviceAsset {
    if (device.vendor !== Vendors.GOOGLE) {
      return device;
    }

    return {
      ...device,
      vendorProperties: {
        ...device.vendorProperties,
        [Vendors.GOOGLE]: {
          ...device.vendorProperties[Vendors.GOOGLE],
          lastUpdated: parseDate(
            device.vendorProperties?.[Vendors.GOOGLE]?.lastUpdated
          ),
          lastSync: parseDate(
            device.vendorProperties?.[Vendors.GOOGLE]?.lastSync
          ),
          autoUpdateExpiration: parseDate(
            device.vendorProperties?.[Vendors.GOOGLE]?.autoUpdateExpiration
          ),
        } as GoogleDeviceData,
      },
    };
  }

  public getDeviceStateAnalysis(
    activeFilters?: SelectedFilterField[]
  ): Observable<ApiResponse<DeviceStateAnalysisAsset>> {
    // Map vendorProperties.google.orgUnitPath to groupingValue for the state-analysis API
    // The DeviceStateAnalysisAsset entity uses 'groupingValue' as its column name
    const mappedFilters = activeFilters?.map((f) => {
      if (f?.field?.name === 'vendorProperties.google.orgUnitPath') {
        return {
          ...f,
          field: {
            ...f.field,
            name: 'groupingValue',
          },
        };
      }
      return f;
    });

    // Build query params like getDevices() does
    const params: Record<string, string> = {};
    if (mappedFilters?.length) {
      params.activeFilters = JSON.stringify(mappedFilters);
    }
    const paramsQueryString = getQueryStringFromParams(params);

    return this.httpClient
      .get<DeviceStateAnalysisAsset[]>(
        `${this.urlBuilder.backendApi('device-assets', 'state-analysis')}?${paramsQueryString}`,
        httpOptions
      )
      .pipe(
        map(
          (
            res: DeviceStateAnalysisAsset[]
          ): ApiResponse<DeviceStateAnalysisAsset> => ({
            success: true,
            data: res,
          })
        ),
        catchError((error) => {
          // biome-ignore lint/suspicious/noConsole: Error logging for API failure visibility
          console.error('Error fetching state analysis for devices:', error);
          return of({
            success: false,
            data: [],
            error: error.message || 'An error occurred',
          } as ApiResponse<DeviceStateAnalysisAsset>);
        })
      );
  }

  public getLatestOsVersions(): Observable<Record<Vendor, number> | null> {
    return this.httpClient
      .get<Record<Vendor, number> | null>(
        this.urlBuilder.backendApi('device-assets', 'latest-versions'),
        httpOptions
      )
      .pipe(
        catchError((error) => {
          // biome-ignore lint/suspicious/noConsole: Error logging for API failure visibility
          console.error('Error fetching latest os versions:', error);
          return of(null);
        })
      );
  }

  public getLatestStableOsVersions(): Observable<Record<
    Vendor,
    Record<string, string>
  > | null> {
    return this.httpClient
      .get<Record<Vendor, Record<string, string>> | null>(
        this.urlBuilder.backendApi('device-assets', 'latest-stable-versions'),
        httpOptions
      )
      .pipe(
        catchError((error) => {
          // biome-ignore lint/suspicious/noConsole: Error logging for API failure visibility
          console.error('Error fetching latest os versions:', error);
          return of(null);
        })
      );
  }

  public getDevicesHealthSummary(
    deviceStateAnalysis: DeviceStateAnalysisAsset[],
    latestOsVersions: Record<Vendor, number>
  ): {
    devicesbatteryHealthSummary: DevicesBatteryHealthSummary;
    devicesVersionSummary: DevicesVersionSummary;
    devicesLastActiveSummary: DevicesLastActiveSummary;
    devicesAueSummary: DevicesAutoUpdateExpirySummary;
  } {
    const devicesbatteryHealthSummary: DevicesBatteryHealthSummary = {
      poorBatteryHealth: 0,
      storageMoreThan80Full: 0,
      moreThan3OsVersionsBehind: 0,
      autInNext90Days: 0,
    };

    const latestChromeOsVersion = latestOsVersions[Vendors.GOOGLE];

    const devicesVersionSummary: DevicesVersionSummary = {
      [VersionInfo.LATEST_OS]: 0,
      [VersionInfo.ONE_OS_BEHIND]: 0,
      [VersionInfo.TWO_OS_BEHIND]: 0,
      [VersionInfo.THREE_PLUS_OS_BEHIND]: 0,
    };

    const devicesLastActiveSummary: DevicesLastActiveSummary = {
      [DeviceLastActiveTimeframe.NotActive]: 0,
      [DeviceLastActiveTimeframe.SevenDays]: 0,
      [DeviceLastActiveTimeframe.FourteenDays]: 0,
      [DeviceLastActiveTimeframe.ThirtyDays]: 0,
      [DeviceLastActiveTimeframe.Unknown]: 0,
    };

    const devicesAueSummary: DevicesAutoUpdateExpirySummary = {
      [AueTimeFrame.PastAUE]: 0,
      [AueTimeFrame.LessThan3Months]: 0,
      [AueTimeFrame.LessThan6Months]: 0,
      [AueTimeFrame.LessThan12Months]: 0,
      [AueTimeFrame.GreaterThan12Months]: 0,
      [AueTimeFrame.Unknown]: 0,
    };

    let poorBatteryHealthCount = 0;
    let autInNext90Days = 0;
    let moreThan3OsVersionsBehindCount = 0;
    let totalDevicesCount = 0;

    for (const entry of deviceStateAnalysis) {
      const count = entry.deviceCount ?? 0;
      totalDevicesCount += count;

      // Poor battery health = "Replace Now" (NOW) category to match Battery Health by OU report
      if (entry.batteryHealth === BatteryHealth.NOW) {
        poorBatteryHealthCount += count;
      }

      // Storage usage
      if (
        entry.diskUsed === DeviceDiskUsed.Eighty ||
        entry.diskUsed === DeviceDiskUsed.Ninety
      ) {
        devicesbatteryHealthSummary.storageMoreThan80Full += count;
      }

      // TODO: This bucketing logic duplicates the backend's osVersionStatus filter mapping
      // in devices.service.ts (mapNonColumnFiltersToRealColumns). Consider unifying into a
      // single backend-computed bucket (e.g., add os_version_status to state-analysis SQL)
      // so dashboard and device search always agree by definition.
      const deviceOsVersion = entry.osVersion ? Number(entry.osVersion) : -1;
      const isKnownVersion =
        !Number.isNaN(deviceOsVersion) && deviceOsVersion > 0;

      if (isKnownVersion && deviceOsVersion === latestChromeOsVersion) {
        devicesVersionSummary[VersionInfo.LATEST_OS] += count;
      } else if (
        isKnownVersion &&
        deviceOsVersion === latestChromeOsVersion - 1
      ) {
        devicesVersionSummary[VersionInfo.ONE_OS_BEHIND] += count;
      } else if (
        isKnownVersion &&
        deviceOsVersion === latestChromeOsVersion - 2
      ) {
        devicesVersionSummary[VersionInfo.TWO_OS_BEHIND] += count;
      } else {
        // Includes: versions 3+ behind, null/empty, unparseable — matches
        // device search where osMajorVersion defaults to -1 (all satisfy <= latest-3)
        moreThan3OsVersionsBehindCount += count;
        devicesVersionSummary[VersionInfo.THREE_PLUS_OS_BEHIND] += count;
      }

      // Map device last active timeframes to exclusive date ranges for the dashboard
      // Backend provides: SevenDays (8-14 days), FourteenDays (15-30 days), ThirtyDays (31+ days)
      const timeframe =
        entry.deviceLastActiveTimeframe ?? DeviceLastActiveTimeframe.Unknown;
      devicesLastActiveSummary[timeframe as keyof DevicesLastActiveSummary] +=
        count;

      // Dashboard uses Relative Time view only
      if (entry.aueTimeframeRelative === AueTimeFrame.LessThan3Months) {
        autInNext90Days += count;
      }

      const aueKey = (entry.aueTimeframeRelative ??
        AueTimeFrame.Unknown) as keyof DevicesAutoUpdateExpirySummary;
      devicesAueSummary[aueKey] += count;
    }

    // Calculate percentages based on total devices
    devicesbatteryHealthSummary.poorBatteryHealth =
      totalDevicesCount > 0
        ? Math.round((poorBatteryHealthCount / totalDevicesCount) * 100)
        : 0;
    devicesbatteryHealthSummary.autInNext90Days =
      totalDevicesCount > 0
        ? Math.round((autInNext90Days / totalDevicesCount) * 100)
        : 0;
    devicesbatteryHealthSummary.moreThan3OsVersionsBehind =
      totalDevicesCount > 0
        ? Math.round((moreThan3OsVersionsBehindCount / totalDevicesCount) * 100)
        : 0;

    return {
      devicesbatteryHealthSummary,
      devicesVersionSummary,
      devicesLastActiveSummary,
      devicesAueSummary,
    };
  }

  public refreshDevices(activeFilters?: SelectedFilterField[]): void {
    this.getDevices({ activeFilters }).subscribe({
      error: () => {
        // fire-and-forget refresh; surface failures via the SSRM
        // path's errorRetrievingData binding instead.
      },
    });
  }

  public getDeviceById(assetId: string): Observable<DeviceAsset | null> {
    return this.store.select(selectCustomerSettings).pipe(
      switchMap((customerData) => {
        // TODO: What happens when we have a microsoft only customer? This needs to check providers to make sure we have any provider id
        if (!customerData?.googleCustomerId) {
          return of(null);
        }

        return this.httpClient
          .get<DeviceAsset | null>(
            this.urlBuilder.backendApi('device-assets', assetId),
            httpOptions
          )
          .pipe(
            map((device: DeviceAsset | null) => {
              if (device) {
                return this.mapDeviceAsset(device);
              }
              return device;
            }),
            catchError((error) => {
              // console.error('Error fetching device by ID:', error);
              return of(null);
            })
          );
      }),
      catchError((error) => {
        // console.error('Failed to retrieve device by ID:', error);
        return of(null);
      })
    );
  }

  public getDeviceToOuPreview(deviceIds: string[]): Observable<Array<{
    deviceId: string;
    currentOrgUnitPath: string;
    orgUnitPath: string;
  }> | null> {
    const maxResults = deviceIds.length;
    const startRow = 0;

    const paramsQueryString = getQueryStringFromParams({
      maxResults,
      startRow,
      endRow: startRow + maxResults,
      activeFilters: deviceIds,
    });
    return this.httpClient
      .get<{
        result: Array<{
          deviceId: string;
          currentOrgUnitPath: string;
          orgUnitPath: string;
        }>;
      }>(
        `${this.urlBuilder.backendApi('device-assets', 'last-user-ou')}?${paramsQueryString}`,
        httpOptions
      )
      .pipe(
        map((response) => response.result || []),
        catchError((error) => {
          // console.error('Error getting device-to-OU preview:', error);
          return of(null);
        })
      );
  }

  public updateDevices(
    updates: Record<string, unknown>[],
    jobType?: string
  ): Observable<{ success: boolean; id: number } | null> {
    const body = {
      updates,
      ...(jobType ? { jobType } : {}),
    };

    return this.httpClient
      .patch<{ success: boolean; id: number } | null>(
        this.urlBuilder.backendApi('device-assets', ''),
        body,
        httpOptions
      )
      .pipe(
        catchError((error) => {
          // console.error('Error updating devices:', error);
          return of(null);
        })
      );
  }

  /**
   * Phase 10 of EDUS-2486 — selection-aware bulk update. Body collapses
   * from `{updates: [...30 MB...]}` to `{useSelection: true, updateSpec,
   * jobType}` (~200 bytes regardless of selection size). Backend reads
   * the affected ids from the user's Redis selection set via
   * gridSessionId header + viewKey query param.
   */
  public updateDevicesUsingSelection(
    updateSpec: Record<string, unknown>,
    jobType: string | undefined,
    gridSessionId: string
  ): Observable<{ success: boolean; id: number } | null> {
    const body = {
      useSelection: true,
      updateSpec,
      ...(jobType ? { jobType } : {}),
    };
    const url = `${this.urlBuilder.backendApi('device-assets', '')}?viewKey=${encodeURIComponent(DEVICE_SEARCH_VIEW_KEY)}`;
    return this.httpClient
      .patch<{ success: boolean; id: number } | null>(
        url,
        body,
        httpOptionsWithSession(gridSessionId)
      )
      .pipe(catchError(() => of(null)));
  }

  /**
   * EDUS-2486 Hit-list #6b — selection-aware refresh dispatch. The
   * navbar's "Refresh Selected" item used to materialize selectedIds
   * client-side; in the SSRM model the client only knows the count,
   * not the ids, so we send `{useSelection: true, jobType?}` and the
   * backend resolves the affected device list from the user's Redis
   * selection set.
   *
   * Backend caps the resolved set at 500 (refresh dispatches a
   * per-device API call; not safe to remove). Selections >500 reject
   * with HTTP 400 — same as the explicit-ids path's existing cap.
   */
  public forceRefreshUsingSelection(
    gridSessionId: string,
    correlationId?: string,
    jobType?: JobType
  ): Observable<{ jobId: string | null; error?: string }> {
    const body = {
      useSelection: true,
      ...(correlationId ? { correlationId } : {}),
      ...(jobType ? { jobType } : {}),
    };
    const url = `${this.urlBuilder.backendApi('device-assets', 'refresh-cache')}?viewKey=${encodeURIComponent(DEVICE_SEARCH_VIEW_KEY)}`;
    return this.httpClient
      .post<{ jobId: string | null }>(
        url,
        body,
        httpOptionsWithSession(gridSessionId)
      )
      .pipe(
        catchError((error: unknown) => {
          // biome-ignore lint/suspicious/noConsole: refresh dispatch diagnostic
          console.error('forceRefreshUsingSelection failed:', error);
          return of({
            jobId: null,
            error:
              (error as { error?: { message?: string } })?.error?.message ??
              (error as { message?: string })?.message ??
              'Force-refresh request failed',
          });
        })
      );
  }

  /**
   * EDUS-2486 Hit-list #6b — selection-aware OU preview. Mirrors
   * `getDeviceToOuPreview` but resolves the device id list server-side
   * from Redis instead of sending it in the query string.
   */
  public getDeviceToOuPreviewUsingSelection(
    gridSessionId: string
  ): Observable<Array<{
    deviceId: string;
    currentOrgUnitPath: string;
    orgUnitPath: string;
  }> | null> {
    const url = `${this.urlBuilder.backendApi('device-assets', 'last-user-ou')}?useSelection=true&viewKey=${encodeURIComponent(DEVICE_SEARCH_VIEW_KEY)}`;
    return this.httpClient
      .get<{
        result: Array<{
          deviceId: string;
          currentOrgUnitPath: string;
          orgUnitPath: string;
        }>;
      }>(url, httpOptionsWithSession(gridSessionId))
      .pipe(
        map((response) => response.result || []),
        catchError(() => of(null))
      );
  }

  /**
   * EDUS-2486 Hit-list #6b — selection-aware OU sync dispatch. The
   * backend resolves the device id list from Redis and skips devices
   * that don't need moving (EDUS-2625).
   */
  public syncDevicesToLastUserOuUsingSelection(
    gridSessionId: string
  ): Observable<unknown | null> {
    const body = { useSelection: true };
    const url = `${this.urlBuilder.backendApi('device-assets', 'last-user-ou')}?viewKey=${encodeURIComponent(DEVICE_SEARCH_VIEW_KEY)}`;
    return this.httpClient
      .patch<unknown | null>(url, body, httpOptionsWithSession(gridSessionId))
      .pipe(catchError(() => of(null)));
  }

  public updateDeviceInfo(
    assetId: string,
    updatedData: Record<string, unknown>
  ): Observable<{ success: boolean; id: number } | null> {
    return this.httpClient
      .patch<{ success: boolean; id: number } | null>(
        this.urlBuilder.backendApi('device-assets', assetId),
        updatedData,
        httpOptions
      )
      .pipe(
        catchError((error) => {
          // console.error('Error updating device:', error);
          return of(null);
        })
      );
  }

  public getDeviceSessions(
    assetId: string
  ): Observable<Record<string, unknown>[] | null> {
    return this.store.select(selectCustomerSettings).pipe(
      switchMap((customerData) => {
        // TODO: What happens when we have a microsoft only customer? This needs to check providers to make sure we have any provider id
        if (!customerData?.googleCustomerId) {
          return of(null);
        }

        return this.httpClient
          .get<Record<string, unknown>[] | null>(
            this.urlBuilder.backendApi('device-assets', `${assetId}/sessions`),
            httpOptions
          )
          .pipe(
            map((deviceSessions: Record<string, unknown>[] | null) => {
              return deviceSessions;
            }),
            catchError((error) => {
              // console.error('Error fetching device by ID:', error);
              return of(null);
            })
          );
      }),
      catchError((error) => {
        // console.error('Failed to retrieve device by ID:', error);
        return of(null);
      })
    );
  }

  /**
   * Get user sessions by email address
   * This consolidates the Google User ID lookup and session retrieval into one call
   */
  public getUserSessionsByEmail(
    email: string
  ): Observable<Record<string, unknown>[] | null> {
    return this.store.select(selectCustomerSettings).pipe(
      switchMap((customerData) => {
        // TODO: What happens when we have a microsoft only customer? This needs to check providers to make sure we have any provider id
        if (!customerData?.googleCustomerId) {
          return of(null);
        }

        return this.httpClient
          .get<Record<string, unknown>[] | null>(
            this.urlBuilder.backendApi(
              'device-assets',
              `sessions/by-email/${encodeURIComponent(email)}`
            ),
            httpOptions
          )
          .pipe(
            map((userSessions: Record<string, unknown>[] | null) => {
              return userSessions;
            }),
            catchError((error) => {
              // console.error('Error fetching user sessions by email:', error);
              return of(null);
            })
          );
      }),
      catchError((error) => {
        // console.error('Failed to retrieve user sessions by email:', error);
        return of(null);
      })
    );
  }

  public getDeviceLogs(
    assetId: string
  ): Observable<Record<string, unknown>[] | Record<string, unknown> | null> {
    return this.store.select(selectCustomerSettings).pipe(
      switchMap((customerData) => {
        // TODO: What happens when we have a microsoft only customer? This needs to check providers to make sure we have any provider id
        if (!customerData?.googleCustomerId) {
          return of(null);
        }

        return this.httpClient
          .get<Record<string, unknown>[] | Record<string, unknown> | null>(
            this.urlBuilder.backendApi('device-assets', `${assetId}/logs`),
            httpOptions
          )
          .pipe(
            map(
              (
                deviceLogs:
                  | Record<string, unknown>[]
                  | Record<string, unknown>
                  | null
              ) => {
                return deviceLogs;
              }
            ),
            catchError((error) => {
              // console.error('Error fetching device by ID:', error);
              return of(null);
            })
          );
      }),
      catchError((error) => {
        // console.error('Failed to retrieve device by ID:', error);
        return of(null);
      })
    );
  }

  /**
   * Force-refresh specific devices by dispatching a Device Get Job,
   * bypassing Redis and SQL caches entirely. The backend may return
   * `jobId: null` legitimately when the cache was already fresh and no
   * job was needed; the optional `error` field distinguishes that
   * "cache hit" case from a true HTTP failure so the UI can pick the
   * right snackbar.
   */
  public forceRefreshByIds(
    deviceIds: string[],
    correlationId?: string,
    jobType?: JobType
  ): Observable<{ jobId: string | null; error?: string }> {
    return this.httpClient
      .post<{ jobId: string | null }>(
        this.urlBuilder.backendApi('device-assets', 'refresh-cache'),
        {
          deviceIds,
          ...(correlationId ? { correlationId } : {}),
          ...(jobType ? { jobType } : {}),
        },
        httpOptions
      )
      .pipe(
        catchError((error) => {
          // biome-ignore lint/suspicious/noConsole: Log refresh dispatch failures for debugging
          console.error('forceRefreshByIds failed:', error);
          return of({
            jobId: null,
            error:
              (error as { message?: string })?.message ??
              'Force-refresh request failed',
          });
        })
      );
  }

  /**
   * Refresh device cache for all devices (workflow-based bulk refresh)
   */
  public refreshCache(assetId?: string): Observable<{
    success: boolean;
    id: number;
  }> {
    const refreshEndpoint = assetId
      ? `${assetId}/refresh-cache`
      : 'refresh-cache';

    return this.httpClient.post<{
      success: boolean;
      id: number;
    }>(
      this.urlBuilder.backendApi('device-assets', refreshEndpoint),
      {},
      httpOptions
    );
  }

  validateFilterValues(
    field: string,
    values: string[]
  ): Observable<ValidateFilterValuesResponseDto> {
    return this.httpClient.post<ValidateFilterValuesResponseDto>(
      this.urlBuilder.backendApi('device-assets', 'validate-filter-values'),
      { field, values },
      httpOptions
    );
  }
}
